import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import Big from 'big.js'
import { useTranslation } from 'react-i18next'
import type { OnlineOperationalModifierOption } from '@/libs/helpers/adapters'
import { useMenuItem } from '@/libs/helpers/hooks/useMenuItem'
import type { CartChildItem, CartItem } from '@/libs/helpers/utils'
import { CheckBox, Grid, QuantitySelector, SvgIcon, Text } from '@/tbui'

interface ModifierOptionProps {
  // Used to differentiate which modifier option is selected if there are
  // multiple modifier groups
  selectionId: string
  modifierOption: OnlineOperationalModifierOption
  isDisabled: boolean
  modifiersUpdated: () => void
  selectionMax: number
  // API expects modifierGroupXRefID but we use "entityID" here for consistency
  modifierGroupID: string
  hasNestedModifiers: boolean
  modifierGroupDOMRefs: React.MutableRefObject<Record<string, HTMLDivElement>>
}

const StyledQuantitySelector = styled(QuantitySelector)`
  justify-content: center;
  @media (max-width: ${(props) => props.theme.breakpoints.XS}) {
    gap: 8px;
  }
  i {
    font-size: 30px;
    height: 30px;
    width: 30px;
  }
  p {
    font-size: 16px;
    height: 18px;
  }
  gap: 10px;
`

const StyledCheckbox = styled(CheckBox)`
  i {
    display: block;
  }
`

const StyledGrid = styled(Grid)`
  padding: 12px 0;
  &:not(:last-child) {
    border-bottom: 2px solid ${({ theme }) => theme.palette.GRAY_1};
  }
`

const StyledNestModsGrid = styled(Grid)<{ hasNestedModifiers: boolean }>`
  cursor: ${(props) => (props.hasNestedModifiers ? 'pointer' : 'unset')};
`

const recursivelyAddChildItemsImmutable = (
  modifiers: CartChildItem[],
  modifierInfo: CartChildItem,
  openNestedModifierSelectionId: string | undefined
): CartChildItem[] => {
  // Normal modifier: simply append to the top-level list immutably
  if (openNestedModifierSelectionId == null) {
    return [...modifiers, modifierInfo]
  }

  // Nested modifier: traverse and return a new array with updated nested childItems
  return modifiers.map((modifier) => {
    if (modifier.selectionId === openNestedModifierSelectionId) {
      const newChildItems = modifier.childItems
        ? [...modifier.childItems, modifierInfo]
        : [modifierInfo]
      return {
        ...modifier,
        childItems: newChildItems,
      }
    }

    if (modifier.childItems) {
      return {
        ...modifier,
        childItems: recursivelyAddChildItemsImmutable(
          modifier.childItems,
          modifierInfo,
          openNestedModifierSelectionId
        ),
      }
    }

    return modifier
  })
}

const ModifierOption: FunctionComponent<ModifierOptionProps> = ({
  selectionId,
  modifierOption,
  isDisabled,
  modifiersUpdated,
  selectionMax,
  modifierGroupID,
  hasNestedModifiers,
  modifierGroupDOMRefs,
}) => {
  const [quantity, setQuantity] = useState<number>(0)
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const { t } = useTranslation()

  const {
    modifiers,
    setModifiers,
    errors,
    setShouldDisplayErrors,
    openNestedModifier,
    setOpenNestedModifier,
  } = useMenuItem()
  const price = modifierOption.modifierGroupRefs[modifierGroupID]?.price ?? modifierOption.price

  const addModifier = useCallback(
    (newQuantity: number): CartChildItem[] => {
      const modifierInfo: CartChildItem = {
        price,
        quantity: newQuantity.toFixed(2),
        itemID: modifierOption.XRefID,
        modifierGroupXRefID: modifierGroupID,
        selectionId,
      }
      return recursivelyAddChildItemsImmutable(
        modifiers ?? [],
        modifierInfo,
        openNestedModifier?.selectionId
      )
    },
    [
      modifierGroupID,
      modifierOption.XRefID,
      modifiers,
      openNestedModifier?.selectionId,
      price,
      selectionId,
    ]
  )

  const getSelectedModifier = useCallback(
    (modifiers: CartChildItem[] | undefined): CartChildItem | undefined => {
      let matchedModifier: CartChildItem | undefined
      if (modifiers != null) {
        for (const modifier of modifiers) {
          if (modifier.selectionId === selectionId) {
            matchedModifier = modifier
            break
          }
          matchedModifier = getSelectedModifier(modifier.childItems)
          if (matchedModifier != null) {
            break
          }
        }
      }
      return matchedModifier
    },
    [selectionId]
  )

  const removeModifierImmutable = useCallback(
    (modifiers: CartChildItem[] | undefined): CartItem['childItems'] => {
      if (modifiers == null) {
        return modifiers
      }

      // If the target exists on this level, return a new array without it
      if (modifiers.some((mod) => mod.selectionId === selectionId)) {
        return modifiers.filter((mod) => mod.selectionId !== selectionId)
      }

      // Otherwise, iterate and recurse. As soon as a child's childItems change
      // (meaning removal happened), return a new modifiers array with that
      // modifier replaced. This allows early exit without a flag.
      for (let i = 0; i < modifiers.length; i++) {
        const modifier = modifiers[i]
        if (modifier.childItems) {
          const newChildItems = removeModifierImmutable(modifier.childItems)
          if (newChildItems !== modifier.childItems) {
            const newModifiers = modifiers.slice()
            newModifiers[i] = {
              ...modifier,
              childItems: newChildItems,
            }
            return newModifiers
          }
        }
      }
      return modifiers
    },
    [selectionId]
  )

  const updateModifierImmutable = useCallback(
    (
      modifiers: CartChildItem[] | undefined,
      selectedModifier: CartChildItem
    ): CartChildItem[] | undefined => {
      if (modifiers == null) {
        return modifiers
      }

      // If the target exists on this level, return a new array with it replaced
      const idx = modifiers.findIndex((m) => m.selectionId === selectedModifier.selectionId)
      if (idx !== -1) {
        const newModifiers = modifiers.slice()
        newModifiers[idx] = selectedModifier
        return newModifiers
      }

      // Otherwise, recurse into children and return a new array as soon as an
      // update is detected (early-return to avoid extra work)
      for (let i = 0; i < modifiers.length; i++) {
        const modifier = modifiers[i]
        if (modifier.childItems) {
          const newChildItems = updateModifierImmutable(modifier.childItems, selectedModifier)
          if (newChildItems !== modifier.childItems) {
            const newModifiers = modifiers.slice()
            newModifiers[i] = {
              ...modifier,
              childItems: newChildItems,
            }
            return newModifiers
          }
        }
      }

      return modifiers
    },
    []
  )

  const handleMaxModChange = useCallback(
    (newQuantity: number): void => {
      const selectedModifier = getSelectedModifier(modifiers)
      if (selectedModifier != null) {
        if (newQuantity > 0) {
          let newPrice = selectedModifier.price
          if (selectedModifier.price != null) {
            if (quantity !== 0) {
              newPrice = Big(newQuantity).mul(Big(selectedModifier.price).div(quantity)).toFixed(2)
            }
          }
          const updatedSelectedModifier: CartChildItem = {
            ...selectedModifier,
            quantity: newQuantity.toString(),
            price: newPrice,
          }
          setModifiers(updateModifierImmutable(modifiers, updatedSelectedModifier))
        } else {
          setModifiers(removeModifierImmutable(modifiers))
        }
      } else if (newQuantity > 0) {
        setModifiers(addModifier(newQuantity))
      }
      setQuantity(newQuantity)
      // It is to set state to false without it isSelected is true should be false
      if (newQuantity === 0) {
        setIsSelected(false)
      }
    },
    [
      addModifier,
      getSelectedModifier,
      modifiers,
      quantity,
      removeModifierImmutable,
      setModifiers,
      updateModifierImmutable,
    ]
  )

  const prevModifiersRef = useRef<CartChildItem[] | undefined>(undefined)
  useEffect(() => {
    // Skip on first render: only notify when `modifiers` changes after mount.
    if (prevModifiersRef.current !== undefined && prevModifiersRef.current !== modifiers) {
      modifiersUpdated()
    }
    prevModifiersRef.current = modifiers
  }, [modifiers, modifiersUpdated])

  useEffect(() => {
    if (modifiers != null && modifiers.length > 0) {
      const modifierToUpdate = getSelectedModifier(modifiers)
      if (modifierToUpdate != null) {
        setQuantity(Number(modifierToUpdate.quantity))
        if (modifierToUpdate.modifierGroupXRefID == null) {
          // If modifier doesn't have modifierGroupXRefID then we add it using the prop
          // passed into this component (immutable update)
          const updatedModifier = {
            ...modifierToUpdate,
            modifierGroupXRefID: modifierGroupID,
          }
          setModifiers(updateModifierImmutable(modifiers, updatedModifier))
        }
      }
    }
  }, [getSelectedModifier, modifierGroupID, modifiers, setModifiers, updateModifierImmutable])

  const onIsSelectedChange = useCallback(
    (value: boolean) => {
      setIsSelected(value)
      if (value) {
        handleMaxModChange(1)
      } else {
        const modifier = getSelectedModifier(modifiers)
        handleMaxModChange(Number(modifier?.quantity ?? 0))
      }
    },
    [handleMaxModChange, getSelectedModifier, modifiers]
  )

  const renderInput =
    quantity > 0 ? (
      <StyledQuantitySelector
        quantityMin={0}
        quantityMax={selectionMax === 0 ? 99 : selectionMax}
        initialQuantity={quantity}
        onChange={(newQuantity) => handleMaxModChange(newQuantity)}
        disableAdd={isDisabled || modifierOption.isOutOfStock}
      />
    ) : (
      <StyledCheckbox
        isSelected={isSelected}
        onChange={(e) => {
          onIsSelectedChange(e.target.checked)
        }}
        name={selectionId}
        id={selectionId}
        variant="ADD"
        isDisabled={isDisabled || modifierOption.isOutOfStock}
        iconSize="30px"
      />
    )

  return (
    <StyledGrid template="1fr auto" gap="6px" align="end">
      <Grid
        template="auto 1fr"
        gap="16px"
        align="center"
        data-test={`modifier-option-container-${selectionId}`}
      >
        {renderInput}
        <Grid gap="6px">
          <Text
            type="H4"
            transform="capitalize"
            style={modifierOption.isOutOfStock ? { textDecoration: 'line-through' } : {}}
            data-test={`modifier-option-${selectionId}-label`}
          >
            {modifierOption.label}
          </Text>
        </Grid>
      </Grid>
      <StyledNestModsGrid
        template="1fr auto"
        gap="6px"
        align="end"
        hasNestedModifiers={hasNestedModifiers}
        data-test={`modifier-option-container-${selectionId}-nested-mods`}
        onClick={() => {
          if (!hasNestedModifiers) {
            return
          }
          // Validate current modifiers before opening nested modifier
          if (errors[modifierGroupID]) {
            if (modifierGroupDOMRefs.current[modifierGroupID] != null) {
              setShouldDisplayErrors(true)
              modifierGroupDOMRefs.current[modifierGroupID].scrollIntoView({
                behavior: 'smooth',
              })
            }
            return
          }
          const matchModifier = getSelectedModifier(modifiers)
          setOpenNestedModifier({
            price: matchModifier?.price ?? '0.00',
            quantity: matchModifier?.quantity ?? '0.00',
            itemID: modifierOption.XRefID,
            modifierGroupXRefID: modifierGroupID,
            selectionId,
          })
          // Clear the error display when opening nested modifier
          setShouldDisplayErrors(false)
        }}
      >
        <Text color="TEXT_2" data-test={`modifier-option-${selectionId}-price`}>
          {t('currency', { value: price })}
        </Text>
        {hasNestedModifiers && <SvgIcon name="chevron_right" fontSize="20px" color="PRIMARY" />}
      </StyledNestModsGrid>
    </StyledGrid>
  )
}

export default ModifierOption
